import { INPUT_PHONE_MAX_LENGTH } from '../../constants';
import { phoneNumberMinLength } from '../../utils/shopUtils';

const VAT_REGEX = /^[A-Z]{2}[A-Za-z0-9]{2,12}$/;
const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export interface FormattedAddress {
  city: string;
  postalCode: string;
  street: string;
  country: string;
  streetNumber: string;
  region: string;
  location?: {
    latitude: number;
    longitude: number;
  };
}

export interface LegalInfo {
  companyName: string;
  companyType: string;
  vatNumber: string;
  registreCommerceNumero: string;
  registreCommerceVille: string;
  capitalSocial: string | number;
  address: FormattedAddress | null;
  typedAddress?: string;
}

export interface ShopInfo {
  name: string;
  email: string;
  number: string;
  address: FormattedAddress | null;
  typedAddress?: string;
}

export interface Configurations {
  businessType: string;
  businessProductType: string;
  keyWords?: string;
}


export interface LaunchingFormData {
  legal: LegalInfo;
  shop: ShopInfo;
  configurations: Configurations;
}

export interface ValidationErrors {
  legal?: {
    companyName?: string;
    companyType?: string;
    vatNumber?: string;
    registreCommerceNumero?: string;
    registreCommerceVille?: string;
    capitalSocial?: string;
    address?: string;
  };
  shop?: {
    name?: string;
    email?: string;
    number?: string;
    address?: string;
  };
  configurations?: {
    businessType?: string;
    businessProductType?: string;
  };
}

export const validateVatNumber = (value: string): string | undefined => {
  const normalized = value.trim().toUpperCase();
  if (!VAT_REGEX.test(normalized)) {
    return 'Le numéro de TVA doit commencer par 2 lettres suivies de 2 à 12 caractères alphanumériques';
  }
  return undefined;
};

export const validateEmail = (value: string): string | undefined => {
  const normalized = value.toLowerCase();
  if (!emailRegex.test(normalized)) {
    return 'Format d\'email invalide';
  }
  return undefined;
};

export const validatePhoneNumber = (
  value: string,
  countryCode: { dialCode: string; iso2: string },
): string | undefined => {
  if (!value) {
    return 'Le numéro de téléphone doit être valide';
  }

  const digitsOnly = value.replace(/\D/g, '');

  if (digitsOnly.startsWith('33')) {
    const dialCode = '33';
    const withoutCountryCode = digitsOnly.substring(dialCode.length);
    const minLength = phoneNumberMinLength('fr');
    if (
      withoutCountryCode.length < minLength ||
      withoutCountryCode[0] === '0'
    ) {
      return 'Le numéro de téléphone doit être valide';
    }
  } else {
    if (digitsOnly.length < INPUT_PHONE_MAX_LENGTH) {
      return 'Le numéro de téléphone doit être valide';
    }
  }

  return undefined;
};

export const validateForm = (data: LaunchingFormData): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!data.legal.companyName || data.legal.companyName.trim() === '') {
    errors.legal = {
      ...errors.legal,
      companyName: "Le nom de l'entreprise est requis",
    };
  }
  if (!data.legal.companyType || data.legal.companyType.trim() === '') {
    errors.legal = {
      ...errors.legal,
      companyType: "Le type d'entreprise est requis",
    };
  }
  if (!data.legal.vatNumber || data.legal.vatNumber.trim() === '') {
    errors.legal = {
      ...errors.legal,
      vatNumber: 'Le numéro de TVA est requis',
    };
  } else {
    const vatError = validateVatNumber(data.legal.vatNumber);
    if (vatError) {
      errors.legal = { ...errors.legal, vatNumber: vatError };
    }
  }
  if (
    !data.legal.registreCommerceNumero ||
    data.legal.registreCommerceNumero.trim() === ''
  ) {
    errors.legal = {
      ...errors.legal,
      registreCommerceNumero: 'Le numéro SIREN est requis',
    };
  }
  if (
    !data.legal.registreCommerceVille ||
    data.legal.registreCommerceVille.trim() === ''
  ) {
    errors.legal = {
      ...errors.legal,
      registreCommerceVille: "La ville d'enregistrement est requise",
    };
  }
  if (
    !data.legal.capitalSocial ||
    data.legal.capitalSocial === '' ||
    data.legal.capitalSocial === null ||
    data.legal.capitalSocial === undefined
  ) {
    errors.legal = {
      ...errors.legal,
      capitalSocial: 'Le capital social est requis',
    };
  } else {
    const num =
      typeof data.legal.capitalSocial === 'string'
        ? parseFloat(data.legal.capitalSocial)
        : data.legal.capitalSocial;
    if (isNaN(num) || num < 0) {
      errors.legal = {
        ...errors.legal,
        capitalSocial: 'Le capital social doit être un nombre valide',
      };
    }
  }
  if (
    !data.legal.address ||
    !data.legal.address.city ||
    data.legal.address.city.trim() === ''
  ) {
    errors.legal = { ...errors.legal, address: 'Adresse requise' };
  } else if (
    !data.legal.address.postalCode ||
    data.legal.address.postalCode.trim() === ''
  ) {
    errors.legal = {
      ...errors.legal,
      address: "Le code postal de l'adresse est requis",
    };
  }

  if (!data.shop.name || data.shop.name.trim() === '') {
    errors.shop = { ...errors.shop, name: 'Le nom du magasin est requis' };
  }
  if (!data.shop.email || data.shop.email.trim() === '') {
    errors.shop = { ...errors.shop, email: "L'email est requis" };
  } else {
    const emailError = validateEmail(data.shop.email);
    if (emailError) {
      errors.shop = { ...errors.shop, email: emailError };
    }
  }
  if (!data.shop.number || data.shop.number.trim() === '') {
    errors.shop = {
      ...errors.shop,
      number: 'Le numéro de téléphone est requis',
    };
  }
  if (
    !data.shop.address ||
    !data.shop.address.city ||
    data.shop.address.city.trim() === ''
  ) {
    errors.shop = { ...errors.shop, address: 'Adresse requise' };
  } else if (
    !data.shop.address.postalCode ||
    data.shop.address.postalCode.trim() === ''
  ) {
    errors.shop = {
      ...errors.shop,
      address: "Le code postal de l'adresse est requis",
    };
  }

  if (
    !data.configurations.businessType ||
    data.configurations.businessType.trim() === ''
  ) {
    errors.configurations = { businessType: "Le type d'activité est requis" };
  }

  return errors;
};
